"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get MeetingTranscriptIngestSchema () {
        return MeetingTranscriptIngestSchema;
    },
    get MeetingTranscriptIngestStatusSchema () {
        return MeetingTranscriptIngestStatusSchema;
    },
    get TERMINAL_MEETING_TRANSCRIPT_INGEST_STATUSES () {
        return TERMINAL_MEETING_TRANSCRIPT_INGEST_STATUSES;
    },
    get TranscriptBannerKeySchema () {
        return TranscriptBannerKeySchema;
    },
    get VIDEO_CONFERENCING_PROVIDERS () {
        return VIDEO_CONFERENCING_PROVIDERS;
    },
    get VIDEO_CONFERENCING_PROVIDER_ATTRIBUTION_FIELDS () {
        return VIDEO_CONFERENCING_PROVIDER_ATTRIBUTION_FIELDS;
    },
    get VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES () {
        return VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES;
    },
    get VideoConferenceOptionalSchema () {
        return VideoConferenceOptionalSchema;
    },
    get VideoConferenceSchema () {
        return VideoConferenceSchema;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const VIDEO_CONFERENCING_PROVIDERS = [
    "zoom",
    "teams"
];
const VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES = {
    zoom: {
        long: "Zoom",
        short: "Zoom"
    },
    teams: {
        long: "Microsoft Teams",
        short: "Teams"
    }
};
const VIDEO_CONFERENCING_PROVIDER_ATTRIBUTION_FIELDS = {
    zoom: "video_conference.meeting_uuid",
    teams: "video_conference.meeting_id"
};
const MeetingTranscriptIngestStatusSchema = _zod.z.enum([
    "awaiting",
    "succeeded",
    "too_long",
    "failed"
]);
const TERMINAL_MEETING_TRANSCRIPT_INGEST_STATUSES = [
    "succeeded",
    "too_long",
    "failed"
];
const MeetingTranscriptIngestSchema = _zod.z.object({
    status: MeetingTranscriptIngestStatusSchema,
    error_code: _zod.z.string().optional(),
    updated_at: _zod.z.instanceof(Date)
});
const TranscriptBannerKeySchema = _zod.z.enum([
    "awaiting",
    "too_long",
    "failed",
    "pmi",
    "disconnected",
    "discoverability",
    // Past the provider's expected turnaround with no transcript; carries the manual trigger (PY-12508).
    "delayed",
    // The company disabled video conferencing while this meeting still has a VC link — no transcript will arrive.
    "video_conferencing_disabled"
]);
/**
 * Fields every video conference exposes regardless of provider: the consumer-facing join link, the
 * provider's upstream meeting id (used to address update/delete API calls), and the automatic
 * transcript-ingest state (set by the webhook/worker pipeline once a meeting ends). Transcript ingest
 * lives here, not on the meeting, because it can only ever exist for a meeting with a connected VC.
 * Kept top-level so consumers read `join_url`/`meeting_id` off the union without narrowing.
 * Provider-specific webhook-attribution keys (e.g. Zoom's `meeting_uuid`) live on the variants.
 */ const VideoConferenceBase = _zod.z.object({
    join_url: _zod.z.string(),
    meeting_id: _zod.z.string(),
    transcript_ingest: MeetingTranscriptIngestSchema.optional(),
    // The account the upstream meeting lives on, whose delegated token addresses it. Optional:
    // meetings created before this field read as the creator (see `resolveVideoConferenceOwner`).
    owner_user_id: _zod.z.instanceof(_bson.ObjectId).optional()
});
/**
 * Discriminated union of conferencing payloads. `start_url` is intentionally excluded — it
 * expires every 2 hours and would mislead users who clicked an expired link. Adding a provider
 * is additive: append a `VideoConferenceBase.extend({ provider: z.literal("teams"), ... })`
 * variant with its own provider-specific fields.
 */ /**
 * The two Zoom meeting settings that gate transcript retrieval, as they came back from Zoom
 * (not as we requested them). We always request `use_pmi: false` + `auto_recording: "cloud"`,
 * but an account-level lock can silently override either; we record the effective values so the
 * transcript feature can explain why a transcript won't populate instead of blocking creation.
 * `auto_recording` falls back to "none" for any unexpected value so a surprising Zoom response
 * can never fail validation.
 */ const ZoomMeetingSettingsSchema = _zod.z.object({
    use_pmi: _zod.z.boolean(),
    auto_recording: _zod.z.enum([
        "local",
        "cloud",
        "none"
    ]).catch("none"),
    // Account/user-level recording setting, NOT echoed on the create response — snapshotted from the
    // user's Zoom settings at create. Optional: absent on older meetings and when the read failed.
    audio_transcript: _zod.z.boolean().optional()
});
/**
 * The Teams setting gating transcript retrieval, as echoed by Graph at create (not as we requested
 * it). We always request `recordAutomatically: true`, but tenant meeting policy can silently strip
 * it; we record the effective value so the transcript feature can explain a missing transcript.
 */ const TeamsMeetingSettingsSchema = _zod.z.object({
    record_automatically: _zod.z.boolean()
});
const VideoConferenceSchema = _zod.z.discriminatedUnion("provider", [
    VideoConferenceBase.extend({
        provider: _zod.z.literal("zoom"),
        host_id: _zod.z.string(),
        meeting_uuid: _zod.z.string(),
        password: _zod.z.string().optional(),
        settings: ZoomMeetingSettingsSchema
    }),
    // Teams' webhook-attribution key is the base `meeting_id` (Graph onlineMeeting id — globally
    // unique, unlike Zoom's recycled numeric id), so its lookup index is provider-scoped.
    VideoConferenceBase.extend({
        provider: _zod.z.literal("teams"),
        settings: TeamsMeetingSettingsSchema,
        // Graph change-notification subscription watching this meeting's transcripts; renewed via
        // lifecycle notifications, unset once the janitor deletes it. Internal-only (not in contracts).
        transcript_subscription: _zod.z.object({
            subscription_id: _zod.z.string(),
            expires_at: _zod.z.instanceof(Date)
        }).optional()
    })
]);
const VideoConferenceOptionalSchema = VideoConferenceSchema.optional();
 // No transcript-locator schema here on purpose: each provider declares its own next to its class and
 // hands it to the base as a type parameter, never persisting or serializing it.
